import { Fragment, useState, useEffect } from 'react'
import { Dialog, Transition } from '@headlessui/react'
import { RotateCcw, XCircle } from 'lucide-react'

export default function ReviewStepModal({
  open,
  setOpen,
  defaultCloseMessage = 'Your request has been closed.',
  onConfirm,
  loading = false,
}) {
  const [selected, setSelected] = useState(null);
  const [message, setMessage] = useState('');

  useEffect(() => {
    if (!open) {
      setSelected(null);
      setMessage('');
    }
  }, [open]);

  const handleSelect = (action) => {
    setSelected(action);
    if (action === 'reject_resubmit') {
      setMessage('');
    } else {
      setMessage(defaultCloseMessage);
    }
  }

  const handleConfirm = () => {
    if (!selected) return;
    if (selected === 'reject_resubmit' && !message.trim()) return;
    onConfirm(selected, message);
  }

  const handleClose = () => {
    setOpen(false);
  }

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={handleClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-100"
          enterFrom="opacity-0"
          enterTo="opacity-90"
          leave="ease-in duration-100"
          leaveFrom="opacity-90"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
          <div className="flex min-h-full items-start justify-center p-4 text-center sm:items-center sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-100"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-100"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white dark:bg-zinc-800 px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-md sm:p-6">
                <div>
                  <Dialog.Title as="h3" className="text-base font-semibold leading-6 text-gray-900 dark:text-white">
                    Review Submission
                  </Dialog.Title>
                  <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                    Choose how to handle this step submission.
                  </p>
                </div>

                <div className="mt-5 space-y-3">
                  {/* Return for resubmission */}
                  <button
                    type="button"
                    disabled={loading}
                    onClick={() => handleSelect('reject_resubmit')}
                    className={`w-full text-left rounded-lg border-2 p-4 transition-colors ${
                      selected === 'reject_resubmit'
                        ? 'border-amber-500 bg-amber-50 dark:bg-amber-900/30'
                        : 'border-gray-200 dark:border-zinc-600 hover:border-amber-300 hover:bg-amber-50/50 dark:hover:bg-amber-900/20'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <RotateCcw className="h-6 w-6 text-amber-600 shrink-0" />
                      <div>
                        <p className="text-sm font-semibold text-gray-900 dark:text-white">Return for Resubmission</p>
                        <p className="text-sm text-gray-500 dark:text-gray-400">Send the step back for the tenant to redo</p>
                      </div>
                    </div>
                  </button>

                  {/* Close request */}
                  <button
                    type="button"
                    disabled={loading}
                    onClick={() => handleSelect('reject_outcome')}
                    className={`w-full text-left rounded-lg border-2 p-4 transition-colors ${
                      selected === 'reject_outcome'
                        ? 'border-red-500 bg-red-50 dark:bg-red-900/30'
                        : 'border-gray-200 dark:border-zinc-600 hover:border-red-300 hover:bg-red-50/50 dark:hover:bg-red-900/20'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <XCircle className="h-6 w-6 text-red-600 shrink-0" />
                      <div>
                        <p className="text-sm font-semibold text-gray-900 dark:text-white">Close Request</p>
                        <p className="text-sm text-gray-500 dark:text-gray-400">Reject as a negative outcome and end the task</p>
                      </div>
                    </div>
                  </button>
                </div>

                {/* Message textarea */}
                {selected && (
                  <div className="mt-4">
                    <label htmlFor="review_message" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Message to customer {selected === 'reject_resubmit' && <span className="text-red-500">*</span>}
                    </label>
                    <textarea
                      id="review_message"
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      placeholder={selected === 'reject_resubmit' ? 'Explain what needs to be corrected...' : ''}
                      rows={3}
                      className="block w-full rounded-md border-0 py-1.5 text-gray-900 dark:text-white dark:bg-zinc-700 shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-zinc-600 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-gray-400 sm:text-sm sm:leading-6"
                    />
                  </div>
                )}

                <div className="mt-5 sm:flex sm:flex-row-reverse">
                  <button
                    type="button"
                    disabled={loading || !selected || (selected === 'reject_resubmit' && !message.trim())}
                    className="inline-flex w-full justify-center rounded-md bg-gray-900 dark:bg-white dark:text-gray-900 dark:hover:bg-gray-200 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-gray-800 sm:ml-3 sm:w-auto disabled:opacity-50"
                    onClick={handleConfirm}
                  >
                    {loading ? 'Processing...' : 'Confirm'}
                  </button>
                  <button
                    type="button"
                    disabled={loading}
                    className="mt-3 inline-flex w-full justify-center rounded-md bg-white dark:bg-zinc-700 px-3 py-2 text-sm font-semibold text-gray-900 dark:text-white shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-zinc-600 hover:bg-gray-50 dark:hover:bg-zinc-600 sm:mt-0 sm:w-auto"
                    onClick={handleClose}
                  >
                    Cancel
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  )
}
